// NFS-e (plan step 5.3; D52): every paid invoice gets a Brazilian service invoice, domestic
// (BRL) or export (USD). The global "automatic NFS-e" switch is off at launch: paid invoices
// then wait here as "issue by hand", and staff record the number the national portal gave.
// With the switch on (it needs the A1 certificate) the API issues them; rejected and failed ones
// come back here with the national API's codes. Every action takes a reason and is audited.
// Console D's list (APS-412, admin-d.md): a strip of counts, the notes as a table with status
// words, a row's actions behind "⋯" when it has more than one, each in its form window; the
// switch and the certificate as `.kv` above the list.
import { KV, PageHead, Section, SelectChip, Status, Strip } from '@apsio/ui'
import { useQuery } from '@tanstack/react-query'
import { type FormEvent, Fragment, useId, useState } from 'react'
import {
  cancelNfse,
  downloadNfse,
  type NfseNote,
  type NfseStatus,
  type NfseView,
  nfseQuery,
  recordNfseCancellation,
  recordNfseNumber,
  sendNfse,
  setNfseAutomatic,
  useAdminMutation,
} from '../api/queries'
import { ActionForm } from '../components/ActionForm'
import {
  DataTable,
  FormWindow,
  IdText,
  NotConfigured,
  ReasonField,
  RowMenu,
  StatusWord,
} from '../components/kit'
import { Empty, ErrorState, Loading } from '../components/states'
import { type MessageId, useI18n } from '../i18n'
import { useFormat } from '../lib/format'
import { MoneyWarns } from './Margin'

export const NFSE_FILTERS = ['open', 'issued', 'all'] as const
export type NfseFilter = (typeof NFSE_FILTERS)[number]

const DONE: NfseStatus[] = ['issued', 'issued_by_hand', 'cancelled']
/** A full refund's cancellation (D97): staff record the portal's protocol, or send it again. */
const CANCEL_OPEN: NfseStatus[] = ['cancel_manual', 'cancel_failed']
/** What staff can record a number for; the API refuses the rest. */
const RECORDABLE: NfseStatus[] = ['manual', 'rejected', 'failed', 'held', 'issued_by_hand']
const SENDABLE: NfseStatus[] = ['manual', 'rejected', 'failed', 'held']

const FILTER_LABEL: Record<NfseFilter, MessageId> = {
  open: 'nfse.filter.open',
  issued: 'nfse.filter.issued',
  all: 'nfse.filter.all',
}

/** What staff can do to a note; each opens its form window with the reason. */
type Action = 'record' | 'send' | 'recordCancel' | 'cancelApi' | 'xml' | 'pdf'

function actionsOf(data: NfseView, n: NfseNote): Action[] {
  const api = data.automatic && data.provider.configured
  return [
    ...(RECORDABLE.includes(n.status) ? (['record'] as const) : []),
    ...(api && SENDABLE.includes(n.status) ? (['send'] as const) : []),
    ...(CANCEL_OPEN.includes(n.status) ? (['recordCancel'] as const) : []),
    ...(n.has_xml ? (['xml'] as const) : []),
    ...(n.has_pdf ? (['pdf'] as const) : []),
    // Cancelling a fiscal note cannot be taken back: last, in the danger tone.
    ...(api && CANCEL_OPEN.includes(n.status) && n.access_key ? (['cancelApi'] as const) : []),
  ]
}

function actionLabel(t: ReturnType<typeof useI18n>['t'], n: NfseNote, a: Action): string {
  switch (a) {
    case 'record':
      return n.status === 'issued_by_hand' ? t('nfse.correct') : t('nfse.record')
    case 'send':
      return t('nfse.send')
    case 'recordCancel':
      return t('nfse.recordCancel')
    case 'cancelApi':
      return t('nfse.cancelApi')
    case 'xml':
      return t('nfse.downloadXml')
    case 'pdf':
      return t('nfse.downloadPdf')
  }
}

function Settings({ data }: { data: NfseView }) {
  const { t, date } = useI18n()
  const toggle = useAdminMutation(setNfseAutomatic, [['nfse']])
  const cert = data.provider.certificate
  const paused = data.provider.problems.length > 0
  return (
    <>
      {/* The one thing on this page to act on: issuing stopped. */}
      <MoneyWarns
        alert
        items={
          paused
            ? [
                {
                  s: 'fire',
                  text: t('nfse.paused', {
                    reasons: data.provider.problems.map((p) => t(`nfse.problem.${p}`)).join('; '),
                  }),
                },
              ]
            : []
        }
      />
      <KV
        label={t('nfse.settingsTitle')}
        items={[
          [
            t('nfse.automatic'),
            !data.automatic ? (
              t('nfse.off')
            ) : paused ? (
              <Status key="auto" s="fire" glyph="×" label={t('nfse.pausedWord')} />
            ) : (
              <Status key="auto" s="res" glyph="✓" label={t('nfse.onWord')} />
            ),
          ],
          [
            t('nfse.provider'),
            data.provider.configured
              ? t('nfse.providerName', {
                  name:
                    data.provider.name === 'national'
                      ? t('nfse.providerNational')
                      : (data.provider.name ?? ''),
                  environment:
                    data.provider.environment === 'production'
                      ? t('nfse.env.production')
                      : data.provider.environment === 'restricted'
                        ? t('nfse.env.restricted')
                        : (data.provider.environment ?? ''),
                })
              : t('nfse.noProvider'),
          ],
          [
            t('nfse.certificate'),
            cert ? t('nfse.validUntil', { date: date(cert.valid_to) }) : t('common.none'),
          ],
        ]}
      />
      {cert ? (
        <p className="money-note mono" translate="no">
          {cert.subject}
        </p>
      ) : null}
      {data.provider.configured || data.automatic ? (
        <div className="money-acts">
          <ActionForm
            label={data.automatic ? t('nfse.turnOff') : t('nfse.turnOn')}
            confirm={data.automatic ? t('nfse.turnOffConfirm') : t('nfse.turnOnConfirm')}
            onSubmit={(reason) => toggle.mutateAsync({ on: !data.automatic, reason })}
          />
        </div>
      ) : (
        <NotConfigured
          missing={
            data.provider.missing.length > 0
              ? t('nfse.missingSettings', { names: data.provider.missing.join(', ') })
              : t('nfse.handOnly')
          }
          task={t('nfse.ownerTask')}
        />
      )}
    </>
  )
}

/** The counts, four cells: what waits on staff, what failed, what to cancel, what is done. */
function Counts({ data }: { data: NfseView }) {
  const { t } = useI18n()
  const { n } = useFormat()
  const c = data.counts
  const problems = c.rejected + c.failed + c.held
  const inProgress = c.pending + c.sending + c.retrying
  return (
    <Strip
      label={t('nfse.title')}
      items={[
        {
          dt: t('nfse.count.manual'),
          dd: n(c.manual),
          d: t('nfse.count.inProgressSub', { count: n(inProgress) }),
        },
        {
          dt: t('nfse.count.problems'),
          dtShort: t('nfse.count.problemsShort'),
          dd: n(problems),
          cur: problems > 0,
        },
        {
          dt: t('nfse.count.toCancel'),
          dd: n(c.cancel_pending + c.cancelling + c.cancel_manual + c.cancel_failed),
        },
        { dt: t('nfse.count.issued'), dd: n(c.issued + c.issued_by_hand) },
      ]}
    />
  )
}

/** The status word, then why, the refund, the protocol, the next try and the API's codes. */
function NoteStatus({ note }: { note: NfseNote }) {
  const { t, dateTime } = useI18n()
  const reason = note.reason?.startsWith('missing:')
    ? t('nfse.reason.missing', { fields: note.reason.slice('missing:'.length) })
    : note.reason === 'switch_off'
      ? t('nfse.reason.switchOff')
      : note.reason === 'dps_sent'
        ? t('nfse.reason.dpsSent')
        : note.reason === 'config_changed'
          ? t('nfse.reason.configChanged')
          : note.reason === 'refunded'
            ? t('nfse.reason.refunded')
            : null
  const lines = [
    reason,
    note.refund_kind === 'partial'
      ? t('nfse.partialRefund', { cents: (note.refunded_cents ?? 0) / 100 })
      : null,
    note.status === 'cancel_manual' ? t('nfse.cancelInPortal') : null,
    note.cancel_protocol ? t('nfse.protocol', { protocol: note.cancel_protocol }) : null,
    note.status === 'retrying' && note.next_attempt_at
      ? t('nfse.next', { time: dateTime(note.next_attempt_at) })
      : null,
  ].filter((l): l is string => l !== null)
  return (
    <>
      <StatusWord kind="nfse" status={note.status} />
      {lines.map((l) => (
        <span key={l} className="t2">
          {l}
        </span>
      ))}
      {note.last_errors && !DONE.includes(note.status)
        ? note.last_errors.map((e) => (
            <span key={`${e.code}${e.message}`} className="t2">
              <span className="mono" translate="no">
                {e.code}
              </span>{' '}
              {e.message}
            </span>
          ))
        : null}
    </>
  )
}

/** One action on one note, in its form window under the table. */
function NoteAction({
  note,
  action,
  onDone,
}: {
  note: NfseNote
  action: Action
  onDone: () => void
}) {
  const { t } = useI18n()
  const record = useAdminMutation(recordNfseNumber, [['nfse']])
  const send = useAdminMutation(sendNfse, [['nfse']])
  const recordCancel = useAdminMutation(recordNfseCancellation, [['nfse']])
  const cancelAgain = useAdminMutation(cancelNfse, [['nfse']])
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>()
  const ids = { number: useId(), protocol: useId(), checked: useId() }
  const id = note.stripe_invoice_id
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    const why = reason.trim()
    setBusy(true)
    setError(undefined)
    try {
      if (action === 'record')
        await record.mutateAsync({
          invoiceId: id,
          number: String(form.get('number') ?? '').trim(),
          reason: why,
          portalChecked: form.get('portal_checked') === 'on',
        })
      else if (action === 'send') await send.mutateAsync({ invoiceId: id, reason: why })
      else if (action === 'recordCancel')
        await recordCancel.mutateAsync({
          invoiceId: id,
          protocol: String(form.get('protocol') ?? '').trim(),
          reason: why,
        })
      else if (action === 'cancelApi') await cancelAgain.mutateAsync({ invoiceId: id, reason: why })
      else await downloadNfse(id, action, why)
      onDone()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }
  const confirm: Record<Action, MessageId> = {
    record: 'nfse.recordConfirm',
    send: 'nfse.sendConfirm',
    recordCancel: 'nfse.recordCancelConfirm',
    cancelApi: 'nfse.cancelApiConfirm',
    xml: 'nfse.download',
    pdf: 'nfse.download',
  }
  return (
    <FormWindow
      title={t('nfse.actionTitle', {
        action: actionLabel(t, note, action),
        invoice: note.invoice_number ?? id,
      })}
      submitLabel={t(confirm[action])}
      danger={action === 'cancelApi'}
      busy={busy}
      onSubmit={submit}
      onCancel={onDone}
      alert={error ? <ErrorState error={error} /> : null}
    >
      {action === 'record' && note.dps_sent && note.status !== 'issued_by_hand' ? (
        <div className="wide field-d">
          <p role="note" className="money-note">
            <Status s="miss" glyph="!" label={t('nfse.checkPortal', { dps: note.dps_id ?? '' })} />
          </p>
          <label className="nf-check" htmlFor={ids.checked}>
            <input id={ids.checked} type="checkbox" name="portal_checked" required />
            <span>{t('nfse.portalChecked', { dps: note.dps_id ?? '' })}</span>
          </label>
        </div>
      ) : null}
      {action === 'record' ? (
        <div className="field-d">
          <label className="lbl" htmlFor={ids.number}>
            {t('nfse.numberLabel')}
          </label>
          <input
            id={ids.number}
            name="number"
            className="input mono"
            inputMode="numeric"
            pattern="[0-9]{1,15}"
            required
            defaultValue={note.note_number ?? ''}
          />
        </div>
      ) : null}
      {action === 'recordCancel' ? (
        <div className="field-d">
          <label className="lbl" htmlFor={ids.protocol}>
            {t('nfse.protocolLabel')}
          </label>
          <input
            id={ids.protocol}
            name="protocol"
            className="input mono"
            maxLength={100}
            required
          />
        </div>
      ) : null}
      <ReasonField
        value={reason}
        onChange={setReason}
        hint={action === 'xml' || action === 'pdf' ? t('nfse.downloadHint') : undefined}
      />
    </FormWindow>
  )
}

function Notes({ data, notes }: { data: NfseView; notes: NfseNote[] }) {
  const { t, date } = useI18n()
  const { money } = useFormat()
  const [open, setOpen] = useState<{ id: string; action: Action } | null>(null)
  if (notes.length === 0) return <Empty title={t('nfse.empty')} />
  return (
    <DataTable label={t('nfse.table')} minWidth={980}>
      <thead>
        <tr>
          <th className="f" scope="col">
            {t('nfse.col.invoice')}
          </th>
          <th className="nf-org" scope="col">
            {t('nfse.col.org')}
          </th>
          <th scope="col">{t('nfse.col.paid')}</th>
          <th className="num" scope="col">
            {t('nfse.col.amount')}
          </th>
          <th scope="col">{t('nfse.col.kind')}</th>
          <th className="nf-st" scope="col">
            {t('nfse.col.status')}
          </th>
          <th className="nf-num" scope="col">
            {t('nfse.col.number')}
          </th>
          <th className="last act" scope="col">
            <span className="vh">{t('nfse.col.action')}</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {notes.map((n) => {
          const actions = actionsOf(data, n)
          const invoice = n.invoice_number ?? n.stripe_invoice_id
          const pick = (action: Action) => setOpen({ id: n.stripe_invoice_id, action })
          return (
            <Fragment key={n.stripe_invoice_id}>
              <tr>
                <td className="f mono" translate="no">
                  {n.invoice_number ?? <IdText id={n.stripe_invoice_id} head={7} />}
                </td>
                <td className="wrap nf-org">
                  <span className="nf-clamp" title={n.org_name}>
                    {n.org_name}
                  </span>
                </td>
                <td>{n.paid_at ? date(n.paid_at) : t('common.none')}</td>
                <td className="num">{money(n.amount_paid, n.currency)}</td>
                <td>{n.kind === 'export' ? t('nfse.kind.export') : t('nfse.kind.domestic')}</td>
                <td className="wrap nf-st">
                  <NoteStatus note={n} />
                </td>
                <td className="mono nf-num" translate="no">
                  {n.note_number ?? t('common.none')}
                </td>
                <td className="last act">
                  {actions.length > 0 ? (
                    // One pattern for every row: its actions in the ⋯ menu (Mosaic).
                    <RowMenu
                      label={t('nfse.actions', { invoice })}
                      items={actions.map((a) => ({
                        label: actionLabel(t, n, a),
                        onSelect: () => pick(a),
                        danger: a === 'cancelApi',
                      }))}
                    />
                  ) : null}
                </td>
              </tr>
              {open?.id === n.stripe_invoice_id ? (
                // The row's form opens right under its own row.
                <tr className="edit money-edit">
                  <td colSpan={8}>
                    <NoteAction
                      key={open.action}
                      note={n}
                      action={open.action}
                      onDone={() => setOpen(null)}
                    />
                  </td>
                </tr>
              ) : null}
            </Fragment>
          )
        })}
      </tbody>
    </DataTable>
  )
}

export function NfsePage() {
  const { t } = useI18n()
  const [filter, setFilter] = useState<NfseFilter>('open')
  const nfse = useQuery(nfseQuery)
  const notes =
    nfse.data?.notes.filter((n) =>
      filter === 'all'
        ? true
        : filter === 'issued'
          ? DONE.includes(n.status)
          : !DONE.includes(n.status),
    ) ?? []
  return (
    <main id="main" className="pg">
      <PageHead
        title={t('nfse.title')}
        meta={t('nfse.lede')}
        actions={
          <SelectChip
            label={t('nfse.filter')}
            prefix={t('nfse.filter')}
            value={filter}
            onChange={(v) => setFilter(v as NfseFilter)}
          >
            {NFSE_FILTERS.map((f) => (
              <option key={f} value={f}>
                {t(FILTER_LABEL[f])}
              </option>
            ))}
          </SelectChip>
        }
      />
      {nfse.isPending ? (
        <Loading />
      ) : nfse.isError ? (
        <ErrorState error={nfse.error} retry={() => nfse.refetch()} />
      ) : (
        <>
          <Counts data={nfse.data} />
          <Section title={t('nfse.settingsTitle')}>
            <Settings data={nfse.data} />
          </Section>
          <Section title={t(FILTER_LABEL[filter])}>
            <Notes data={nfse.data} notes={notes} />
            <p className="money-note">{t('nfse.leadNote')}</p>
          </Section>
        </>
      )}
    </main>
  )
}
